import { useMemo } from "react";
import SlideUpDialog from "./SlideUpDialog";
import GenericDialogTitle from "./GenericDialogTitle";
import { Box, DialogContent, Stack, Typography, Tooltip } from "@mui/material";
import ResponsiveQRCode from "./ResponsiveQRCode";
import CopyableTypography from "./CopyableTypography";

type TQRDialogContent = {
  open: boolean;
  handleClose: () => void;
  title: string;
  value: string;
  showQrCodeValue: boolean;
  enableCopy: boolean;
  instruction: string;
  valueLabel: string;
} & Omit<React.ComponentProps<typeof SlideUpDialog>, "children">;

const QRDialogContent = ({
  open,
  handleClose,
  title,
  value,
  showQrCodeValue = true,
  instruction = "Scan this QR code with another device.", //'Scan this QR code with another device to transfer tokens',
  enableCopy = true,
  valueLabel = "",
}: TQRDialogContent) => {
  const valueDisplay = useMemo(
    () =>
      showQrCodeValue && !enableCopy ? (
        <Tooltip
          title={
            <Box sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}>
              {value}
            </Box>
          }
          arrow
          placement="top"
        >
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontFamily: "monospace",
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
            }}
          >
            {valueLabel}: {value}
          </Typography>
        </Tooltip>
      ) : showQrCodeValue && enableCopy ? (
        <CopyableTypography
          displayText={valueLabel.length > 0 ? `${valueLabel}: ${value}` : value}
          text={value}
          sxProps={{ wordBreak: "break-word", whiteSpace: "wrap" }}
        />
      ) : (
        <></>
      ),
    [showQrCodeValue, enableCopy, value, valueLabel]
  );

  return (
    <SlideUpDialog open={open} handleClose={handleClose} title={title} maxWidth="sm" fullWidth>
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center", py: 2 }}>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {instruction}
          </Typography>
          <ResponsiveQRCode value={value} />
          <Box sx={{ width: "100%" }}>{valueDisplay}</Box>
        </Stack>
      </DialogContent>
    </SlideUpDialog>
  );
};

export default QRDialogContent;
